import { useEffect, useRef, useState } from 'react';
import {
  advisePractice,
  eligibleAdviseTeams,
  holdsTeamlessRole,
  needsAdviseTeam,
  practiceLabel
} from '../../../utils/adviseRoles';
import type { Team } from '../../../store/Team';

export interface AdviseTeamState {
  active: boolean;
  teams: Team[];
  helperText?: string;
}

const pickTeamPrompt = (practice?: string, previousPractice?: string): string | undefined => {
  if (!practice) return undefined;
  const pickTeam = `Pick a ${practiceLabel(practice)} team.`;
  return previousPractice && previousPractice !== practice ? `The role changed practice. ${pickTeam}` : pickTeam;
};

export const useAdviseTeam = (
  roleNames: string[],
  teams: Team[],
  selectedTeam: { id: string } | null,
  onClear: () => void
): AdviseTeamState => {
  const [helperText, setHelperText] = useState<string>();
  // The picker never holds two Analysts, so switching practice takes several role changes.
  const clearedPractice = useRef<string | undefined>(undefined);
  const active = needsAdviseTeam(roleNames);
  const options = eligibleAdviseTeams(roleNames, teams);
  const practice = advisePractice(roleNames);

  const promptAfterEarlierClear = () => {
    const pending = active && clearedPractice.current;
    setHelperText(pending ? pickTeamPrompt(practice, clearedPractice.current) : undefined);
    if (practice) clearedPractice.current = undefined;
  };

  useEffect(() => {
    if (!selectedTeam) return promptAfterEarlierClear();
    if (holdsTeamlessRole(roleNames)) return onClear();

    const selectedPractice = teams.find((team) => team.id === selectedTeam.id)?.practice ?? undefined;
    if (!active) {
      if (!selectedPractice) return;
      clearedPractice.current = selectedPractice;
      return onClear();
    }
    if (options.some((team) => team.id === selectedTeam.id)) return;

    setHelperText(pickTeamPrompt(practice, selectedPractice));
    onClear();
  }, [roleNames.join('|')]);

  useEffect(() => {
    if (!selectedTeam) return;
    clearedPractice.current = undefined;
    setHelperText(undefined);
  }, [selectedTeam?.id]);

  return { active, teams: options, helperText };
};
